import FormattedMessage from 'I18n/components/FormattedMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { H6 } from 'foundations-components/transitional/components/H6';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import styled from 'foundations-theming/utils';
import { getButtonVariantProps } from 'pricing-pages-lib/utils/buttonVariantUtils';
import { getI18nText } from 'pricing-pages-lib/utils/languageUtils';
import { trellisBreakpoint } from 'pricing-pages-ui-components/constants/Breakpoints';
import { PriceDisplayWithTerm } from 'pricing-pages-ui-components/price/PriceDisplayWithTerm';
import { FullPriceContainerBlockLarge } from 'pricing-pages-ui-components/price/PriceStyledComponents';
import { useState } from 'react';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import HubSpotVideoPlayer from 'ui-addon-video-player/components/HubSpotVideoPlayer';
import SuiteProductLevelIdentifiersMap from '../../constants/merchCards/SuiteProductLevelIdentifiersMap';
import { SCREENS } from '../../constants/tracking/Screens';
import { SSM0010_RECOMMENDED_STARTER_HEADER_BANNER } from '../../constants/tracking/TrackingSources';
import { SuiteCTA } from '../../features/purchaseMotions/components/SuiteCTA';
import useSuitePurchaseMotions from '../../features/purchaseMotions/hooks/useSuitePurchaseMotions';
import { useGlobalLoading } from '../../hooks/loadingHooks';
import { useCurrencyCodeUnitToDisplay, useDisplayedCurrencyCode } from '../../hooks/useCurrencies';
import { useSuiteRecommendationModelFeedback } from '../../hooks/useRecommendations';
import { trackHoverInteraction, trackVideoInteraction } from '../../utils/tracking/trackingEventUtils';
import { useQueryParamTerm } from '../../utils/urls/appQueryParamUtils';
import SkeletonLoadWrapper from '../common/loading/SkeletonLoadWrapper';
import SuiteTermSelectorContainer from '../merch-card/SuiteTermSelectorContainer';
import { useCrmSuiteBundlePricingMetadata, useCrmSuitePricingInfo } from '../merch-card/price-display/useCrmSuitePricingInfo';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const CopyContainer = styled.div.withConfig({
  displayName: "RecommendedStarterHeaderBanner__CopyContainer",
  componentId: "sc-117acd3-0"
})({
  inlineSize: '45%',
  [trellisBreakpoint.tabletPortraitLarge]: {
    inlineSize: '100%'
  }
});
const VideoContainer = styled.div.withConfig({
  displayName: "RecommendedStarterHeaderBanner__VideoContainer",
  componentId: "sc-117acd3-1"
})(({
  theme
}) => {
  return {
    inlineSize: '50%',
    marginBlock: 'auto',
    marginInlineStart: theme.space[100],
    boxShadow: theme.boxShadow[300],
    borderRadius: theme.borderRadius[100],
    [trellisBreakpoint.tabletPortraitLarge]: {
      display: 'none'
    }
  };
});
const ButtonsContainer = styled.div.withConfig({
  displayName: "RecommendedStarterHeaderBanner__ButtonsContainer",
  componentId: "sc-117acd3-2"
})({
  inlineSize: '70%',
  [trellisBreakpoint.tabletPortraitLarge]: {
    inlineSize: '100%'
  }
});
const RecommendedStarterHeaderBanner = () => {
  const [progress, setProgress] = useState(0);
  const SSM0010_RECOMMENDED_STARTER_VIDEO_NAME = 'SSM0010_recommended_starter_header_video';
  const isAppReady = useGlobalLoading();
  const {
    pricingInfo,
    loading: pricingInfoLoading
  } = useCrmSuitePricingInfo({
    productLevel: STARTER
  });
  const currencyCode = useDisplayedCurrencyCode();
  const currencyCodeUnit = useCurrencyCodeUnitToDisplay();
  const {
    showPricePerMonthPerSeat
  } = useCrmSuiteBundlePricingMetadata({
    productLevel: STARTER
  });
  const {
    setQueryParamTerm,
    queryParamTerm
  } = useQueryParamTerm();
  const purchaseMotions = useSuitePurchaseMotions({
    productLevel: STARTER,
    returnFirstCTAOnly: true
  });
  const {
    onClick
  } = useSuiteRecommendationModelFeedback({
    productLevel: STARTER
  });
  const loading = pricingInfoLoading || !isAppReady;
  const ctaVariantProps = getButtonVariantProps({
    variant: 'block'
  });
  return /*#__PURE__*/_jsx(View, {
    backgroundColor: "fill-primary-subtle",
    children: /*#__PURE__*/_jsxs(Flex, {
      flexDirection: "row",
      paddingBlock: "1100",
      paddingInline: "1000",
      maxInlineSize: "1200px",
      marginInline: "auto",
      justifyContent: "space-between",
      children: [/*#__PURE__*/_jsxs(CopyContainer, {
        children: [/*#__PURE__*/_jsxs(Badge, {
          use: "oz",
          marginBlockEnd: "500",
          display: "block",
          children: [/*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.ssm0010Recommended.recommendedForYou"
          }), ' ', /*#__PURE__*/_jsx(HelpIcon, {
            title: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "pricingPage.pages.ssm0010Recommended.recommendedForYouTooltip"
            }),
            marginInlineStart: "100",
            use: "on-dark",
            tooltipPlacement: "right",
            tooltipProps: {
              use: 'longform',
              onOpenChange: e => {
                if (e.currentTarget.value) {
                  trackHoverInteraction({
                    value: getI18nText(`pricingPage.pages.ssm0010Recommended.recommendedForYouTooltip`, {
                      locale: 'en-us'
                    }),
                    trackingSource: SSM0010_RECOMMENDED_STARTER_HEADER_BANNER
                  });
                }
              }
            }
          })]
        }), /*#__PURE__*/_jsx(H6, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.ssm0010Recommended.upgradeTo"
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "h1",
          typography: "body-700",
          marginBlockEnd: "500",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.ssm0010Recommended.title"
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "p",
          marginBlock: "500",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "pricingPage.pages.ssm0010Recommended.subtitle"
          })
        }), /*#__PURE__*/_jsx(Flex, {
          flexDirection: "column",
          children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
            isLoading: loading,
            heights: [16, 23, 16],
            widths: [60, 70, 130],
            children: pricingInfo && /*#__PURE__*/_jsx(FullPriceContainerBlockLarge, {
              children: /*#__PURE__*/_jsx(PriceDisplayWithTerm, {
                pricingInfo: pricingInfo,
                showPricePerMonthPerSeat: showPricePerMonthPerSeat,
                seleniumId: SuiteProductLevelIdentifiersMap[STARTER],
                currencyCode: currencyCode,
                currencyCodeUnit: currencyCodeUnit
              })
            })
          })
        }), /*#__PURE__*/_jsxs(ButtonsContainer, {
          children: [/*#__PURE__*/_jsx(View, {
            marginBlockStart: "500",
            as: "div",
            children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
              isLoading: !isAppReady,
              children: /*#__PURE__*/_jsx(SuiteTermSelectorContainer, {
                productLevel: STARTER,
                onChange: setQueryParamTerm,
                queryParamTerm: queryParamTerm,
                trackingSource: SSM0010_RECOMMENDED_STARTER_HEADER_BANNER
              })
            })
          }), /*#__PURE__*/_jsx(View, {
            marginBlockStart: "500",
            as: "div",
            children: /*#__PURE__*/_jsx(SkeletonLoadWrapper, {
              isLoading: !isAppReady,
              children: purchaseMotions.map(purchaseMotion => /*#__PURE__*/_jsx("div", {
                children: /*#__PURE__*/_jsx(SuiteCTA, Object.assign({
                  purchaseMotion: purchaseMotion,
                  productLevel: STARTER,
                  trackingSource: SSM0010_RECOMMENDED_STARTER_HEADER_BANNER,
                  screen: SCREENS.RECOMMENDED,
                  onClick: onClick,
                  purchaseMotionLocation: "MERCH_CARD"
                }, ctaVariantProps))
              }, purchaseMotion))
            })
          })]
        })]
      }), /*#__PURE__*/_jsx(VideoContainer, {
        "data-test-id": "recommended-starter-header-banner-video",
        children: /*#__PURE__*/_jsx(HubSpotVideoPlayer, {
          videoId: 172858739586,
          onPlay: () => {
            trackVideoInteraction({
              action: 'play-video',
              name: SSM0010_RECOMMENDED_STARTER_VIDEO_NAME,
              trackingSource: SSM0010_RECOMMENDED_STARTER_HEADER_BANNER,
              videoTimestamp: progress
            });
          },
          onProgress: e => {
            setProgress(e.detail.currentTime);
          },
          onPause: () => {
            trackVideoInteraction({
              action: 'pause-video',
              name: SSM0010_RECOMMENDED_STARTER_VIDEO_NAME,
              trackingSource: SSM0010_RECOMMENDED_STARTER_HEADER_BANNER,
              videoTimestamp: progress
            });
          },
          onFinish: () => {
            trackVideoInteraction({
              action: 'finished-video',
              name: SSM0010_RECOMMENDED_STARTER_VIDEO_NAME,
              trackingSource: SSM0010_RECOMMENDED_STARTER_HEADER_BANNER
            });
          }
        })
      })]
    })
  });
};
export default RecommendedStarterHeaderBanner;